import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
  Box,
  Button,
  Image,
  SxProps,
  Theme,
  Typography,
} from "@outschool/backpack";
import { ContentfulClpEmailCapture } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { useSession } from "@outschool/ui-auth";
import { FormFieldError, Input } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { SubmitHandler } from "react-hook-form";

import { useEmailForm } from "./hooks/useEmailForm";
import BearBookTent from "../images/iterable-email-capture-box-bear-book-tent.webp";
import EmailCaptureBox from "../images/iterable-email-capture-box.webp";

type EmailSubscribeInputs = {
  email: string;
};

export default function IterableEmailCaptureBox({
  sx = {},
  contentfulData,
  isProduction,
  renderImage,
}: {
  sx?: SxProps;
  contentfulData: ContentfulClpEmailCapture;
  isProduction: boolean;
  renderImage?: React.ReactElement;
}) {
  const cookieName =
    "subscribed_to_" +
    contentfulData.name?.trim().toLowerCase().split(" ").join("_");
  const cookie = Cookies.get(cookieName);

  const [submitted, setSubmitted] = useState(cookie === "true");
  const { currentUser } = useSession();
  const [submissionError, setSubmissionError] = useState<string>("");

  const isMobile = useIsMobile();

  const { t } = useTranslation(
    "ui-components-website\\IterableEmailCaptureBox",
  );

  const { register, reset, handleSubmit, errors } = useEmailForm({
    defaultValues: {
      email: currentUser?.email ?? "",
    },
  });

  useEffect(() => {
    if (currentUser?.email) {
      reset({ email: currentUser.email });
    }
  }, [currentUser?.email, reset]);

  useEffect(() => {
    setSubmitted(cookie === "true");
  }, [cookie]);

  const submitEmailToIterable: SubmitHandler<EmailSubscribeInputs> = fields => {
    setSubmissionError("");
    const iterableLink =
      "//links.iterable.com/lists/publicAddSubscriberForm?publicIdString=";
    fetch(
      isProduction
        ? iterableLink + contentfulData.prodIterableAudienceId
        : iterableLink +
            (contentfulData.stagingIterableAudienceId ??
              "2b23cd49-f925-4ab8-9a3c-f89dfc13b7a1"),
      {
        method: "POST",
        redirect: "manual",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(fields),
      },
    )
      .then(response => {
        if (!response.ok && response.type != "opaqueredirect") {
          console.debug(response);
          throw Error(response.statusText);
        }
        setSubmitted(true);
        Cookies.set(cookieName, "true");
      })
      .catch(error => {
        OsPlatform.captureError(error, {
          tags: {
            component: Component.Marketing,
          },
        });
        if (contentfulData.errorMessage) {
          setSubmissionError(contentfulData.errorMessage);
        }
      });
  };

  return (
    <Box
      flex
      sx={[
        {
          padding: isMobile ? "2em" : "3em",
          backgroundImage: `url("${EmailCaptureBox}")`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% auto",
          backgroundColor: "primary.50",
          color: "common.black",
          borderRadius: 20,
          rowGap: "0.5em",
          margin: "auto",
          flexDirection: "column",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {contentfulData.title && (
        <Box
          sx={{
            marginBottom: "0.5em",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography variant={"h2"}>{contentfulData.title}</Typography>
        </Box>
      )}
      <Box flex>
        <Box sx={{ width: isMobile ? "100%" : "60%" }}>
          {contentfulData.bodyContent && (
            <Box
              sx={(theme: Theme) => ({
                padding: "1em 0px",
                "& p": {
                  marginBottom: theme.spacing(16),
                },
              })}
            >
              {documentToReactComponents(contentfulData.bodyContent.json)}
            </Box>
          )}
          <Box>
            {!submitted && (
              <>
                {contentfulData.callToActionText && (
                  <Box
                    sx={{
                      marginBottom: "0.5em",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      {contentfulData.callToActionText}
                    </Typography>
                  </Box>
                )}
                <Box
                  component="form"
                  onSubmit={handleSubmit(submitEmailToIterable)}
                >
                  <Box
                    sx={(theme: Theme) => ({
                      display: "flex",
                      flexDirection: "column",

                      [theme.breakpoints.up("md")]: {
                        flexDirection: "row",
                      },

                      gap: "0.5em",
                    })}
                  >
                    <Input
                      placeholder={t("Enter your email")}
                      // eslint-disable-next-line i18next/no-literal-string
                      inputMode="email"
                      style={{
                        flex: 1,
                        width: "100%",
                        borderRadius: 20,
                      }}
                      {...register("email", { required: true })}
                    />
                    <Button
                      type="submit"
                      size="small"
                      color="primary"
                      variant="contained"
                    >
                      {t("Sign Up")}
                    </Button>
                  </Box>
                  {errors.email && (
                    <FormFieldError>{errors.email.message}</FormFieldError>
                  )}
                  {submissionError && (
                    <FormFieldError>{submissionError}</FormFieldError>
                  )}
                </Box>
              </>
            )}

            {submitted && contentfulData.successMessage && (
              <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
                {contentfulData.successMessage}
              </Typography>
            )}
          </Box>
        </Box>
        {!isMobile && (
          <>
            <Box flex sx={{ width: "40%", justifyContent: "center" }}>
              {!!renderImage ? (
                renderImage
              ) : (
                <Image src={BearBookTent} height="250px" />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
